import { Theme } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import React from "react";
import { useRefresh } from "react-admin";
import {
  TenantRegistrationDto,
  TenantRegistrationStatus,
} from "../../../common/data/service-proxies";
import { ApproveDeletionMenuItem } from "./ApproveDeletionMenuItem";
import { ApproveRegistrationMenuItem } from "./ApproveRegistrationMenuItem";
import { ApproveDeactivationMenuItem } from "./ApproveDeactivationMenuItem";
import { ReactivateTenantRegistrationMenuItem } from "./ReactivateTenantRegistrationMenuItem";

interface Props {
  label: string;
  icon: JSX.Element;
  record?: TenantRegistrationDto;
  resource?: string;
  confirmTitle?: string;
  confirmContent?: string;
  onClick?: (event: React.MouseEvent) => void;
  hidden?: boolean | ((record: TenantRegistrationDto) => boolean);
  status?: TenantRegistrationStatus;
}

const isBoolean = (
  input?: boolean | ((record: TenantRegistrationDto) => boolean)
): input is boolean => {
  return input === true || input === false;
};

const getNewStatus = (record: TenantRegistrationDto) => {
  if (
    record.tenantRegistrationStatus === TenantRegistrationStatus.DeletePending
  ) {
    return TenantRegistrationStatus.Deleted;
  }

  if (
    record.tenantRegistrationStatus ===
    TenantRegistrationStatus.RegistrationPending
  ) {
    return TenantRegistrationStatus.Active;
  }

  if (
    record.tenantRegistrationStatus ===
    TenantRegistrationStatus.DeactivationPending
  ) {
    return TenantRegistrationStatus.Deactivated;
  }
};

const useApproveStyles = makeStyles((theme: Theme) => ({
  approve: {
    color: theme.palette.success.main,
  },
}));

const useRejectStyles = makeStyles((theme: Theme) => ({
  approve: {
    color: theme.palette.error.main,
  },
}));

export const ApproveTenantRegistrationMenuItem = (props: Props) => {
  const { record, resource, onClick, hidden, status, ...rest } = props;
  const approveClasses = useApproveStyles();
  const rejectClasses = useRejectStyles();
  const refresh = useRefresh();

  if (!record) {
    return null;
  }

  if (!resource) {
    return null;
  }

  if (isBoolean(hidden)) {
    if (hidden) {
      return null;
    }
  } else {
    if (hidden && hidden(record)) {
      return null;
    }
  }

  const newStatus = status ?? getNewStatus(record);
  if (
    newStatus === TenantRegistrationStatus.Active &&
    record.tenantRegistrationStatus ===
      TenantRegistrationStatus.RegistrationPending
  ) {
    return (
      <ApproveRegistrationMenuItem
        record={record}
        classes={approveClasses}
        resource={resource}
        onClick={onClick}
        onSuccess={() => refresh()}
        {...rest}
      />
    );
  }

  if (newStatus === TenantRegistrationStatus.Active) {
    return (
      <ReactivateTenantRegistrationMenuItem
        record={record}
        classes={approveClasses}
        resource={resource}
        onClick={onClick}
        onSuccess={() => refresh()}
        {...rest}
      />
    );
  }

  if (newStatus === TenantRegistrationStatus.Deactivated) {
    return (
      <ApproveDeactivationMenuItem
        record={record}
        classes={approveClasses}
        resource={resource}
        onClick={onClick}
        onSuccess={() => refresh()}
        {...rest}
      />
    );
  }

  return (
    <ApproveDeletionMenuItem
      record={record}
      resource={resource}
      classes={rejectClasses}
      onClick={onClick}
      onSuccess={() => refresh()}
      {...rest}
    />
  );
};
